import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import api from "services/api";
import { __ } from "services/Translator";
import HubToast, { showHubToast } from "components/goldenCloverHub/HubToast";
import {
  STORE_FILTERS,
  STORE_MYSTERY_PRIZES,
  mapPrizeToReveal,
} from "./storeMockData";
import { STORE_SVG_DEFS } from "./storeSvgDefs";

function formatCc(value) {
  return Number(value || 0).toLocaleString();
}

// Visually hidden but exposed to screen readers (page SCSS has no shared
// .sr-only util in scope), used for the live-region balance twin.
const SR_ONLY_STYLE = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};

// One key per confirm-modal open, so a retry of the same attempt cannot
// double-charge on the backend.
function makeIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `rk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function CloverIcon({ size = 18 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M12 3.5c2 0 3.5 1.5 3.5 3.5S14 10.5 12 10.5 8.5 9 8.5 7 10 3.5 12 3.5Z"
        fill="currentColor"
        opacity="0.15"
      />
      <path
        d="M10.4 9.6 6.2 7.2c-.5-.3-1.1-.1-1.4.4-.3.5-.1 1.1.4 1.4l4.2 2.4c-.7 1.2-.5 2.8.6 3.7l-2.3 4.1c-.3.5-.1 1.1.4 1.4.5.3 1.1.1 1.4-.4l2.3-4.1c1.1.4 2.4.1 3.2-.8l4.2 2.4c.5.3 1.1.1 1.4-.4.3-.5.1-1.1-.4-1.4l-4.2-2.4c.7-1.2.5-2.8-.6-3.7L18.6 5c.3-.5.1-1.1-.4-1.4-.5-.3-1.1-.1-1.4.4L14.5 8.1c-1.1-.4-2.4-.1-3.2.8Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Branded gold clover crest — the on-brand stand-in for the old 🍀 emoji in
// empty / unavailable states. Exported so the page shell reuses the same mark.
export function CloverCrest({ size = 60 }) {
  return (
    <span className="store-clover-crest" aria-hidden="true">
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 3.5c2 0 3.5 1.5 3.5 3.5S14 10.5 12 10.5 8.5 9 8.5 7 10 3.5 12 3.5Z"
          fill="currentColor"
          opacity="0.2"
        />
        <path
          d="M10.4 9.6 6.2 7.2c-.5-.3-1.1-.1-1.4.4-.3.5-.1 1.1.4 1.4l4.2 2.4c-.7 1.2-.5 2.8.6 3.7l-2.3 4.1c-.3.5-.1 1.1.4 1.4.5.3 1.1.1 1.4-.4l2.3-4.1c1.1.4 2.4.1 3.2-.8l4.2 2.4c.5.3 1.1.1 1.4-.4.3-.5.1-1.1-.4-1.4l-4.2-2.4c.7-1.2.5-2.8-.6-3.7L18.6 5c.3-.5.1-1.1-.4-1.4-.5-.3-1.1-.1-1.4.4L14.5 8.1c-1.1-.4-2.4-.1-3.2.8Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
}

function ArtSymbol({ artId, mode = "main" }) {
  return (
    <svg
      viewBox="0 0 160 120"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      className={`store-art-symbol mode-${mode}`}
      style={{ width: "100%", height: "100%", maxHeight: "100%" }}
    >
      <use href={`#${artId}`} />
    </svg>
  );
}

function CardArt({ item }) {
  if (item.displayMode === "illust") {
    return (
      <div
        className={[
          "store-card__image",
          `cat-${item.category}`,
          item.tierClass ? item.tierClass : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="store-card__fx" />
        {item.tag ? (
          <span className={`store-card__tag ${item.tagClass || ""}`}>{item.tag}</span>
        ) : null}
        <div className="store-card__art">
          <ArtSymbol artId={item.artId} mode="main" />
        </div>
      </div>
    );
  }

  return (
    <div className={`store-card__image typo cat-${item.category}`}>
      <div className="store-card__fx" />
      {item.tag ? (
        <span className={`store-card__tag ${item.tagClass || ""}`}>{item.tag}</span>
      ) : null}
      <div className={`store-card__accent ${item.accentPos || "cn"}`} aria-hidden="true">
        <ArtSymbol artId={item.artId} mode="accent" />
      </div>
      <div className={`store-card__big ${item.bigSize || ""}`}>
        {item.bigText}
        <small>{item.subLabel}</small>
      </div>
    </div>
  );
}

export default function StoreContent({ data: dataProp, isPreviewDemo }) {
  const history = useHistory();
  const mock = useMemo(() => dataProp, [dataProp]);
  const data = dataProp || mock;
  const preview = isPreviewDemo ?? data?.isPreviewDemo ?? false;

  const [balance, setBalance] = useState(() => {
    const raw = data?.balance ?? 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  });
  // Screen-reader twin: updated only once the count-up settles, so SR users
  // hear the final figure rather than every animation frame.
  const [srBalance, setSrBalance] = useState(() => {
    const n = Number(data?.balance ?? 0);
    return Number.isFinite(n) ? n : 0;
  });

  const [activeFilter, setActiveFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [warning, setWarning] = useState("");
  const [revealPrize, setRevealPrize] = useState(null);
  const [revealPhase, setRevealPhase] = useState("none"); // none | pending | revealed
  const [shakingItemId, setShakingItemId] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const closeBtnRef = useRef(null);
  const activeCardRef = useRef(null);
  const cardRefs = useRef([]);
  const idempotencyKeyRef = useRef("");
  const modalRef = useRef(null);

  const items = data?.items || [];
  // Only treat the balance as real when the payload actually carried one.
  // A null/unknown balance must never drive affordability UI or a coin HUD.
  const balanceKnown = data?.balance != null && Number.isFinite(Number(data.balance));

  useEffect(() => {
    if (data?.balance == null) return;
    const n = Number(data.balance);
    if (Number.isFinite(n)) {
      setBalance(n);
      setSrBalance(n);
    }
  }, [data?.balance]);

  const filteredItems = useMemo(() => {
    if (activeFilter === "all") return items;
    return items.filter((item) => item.category === activeFilter);
  }, [items, activeFilter]);

  const filterCounts = useMemo(() => {
    const counts = {};
    for (const item of items) {
      counts[item.category] = (counts[item.category] || 0) + 1;
    }
    return counts;
  }, [items]);

  const animateBalance = useCallback((from, to) => {
    const dur = 800;
    const t0 = performance.now();
    const fromN = Number(from);
    const toN = Number(to);
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - t, 3);
      const value = Math.round(fromN + (toN - fromN) * e);
      setBalance(value);
      if (t < 1) requestAnimationFrame(tick);
      else setSrBalance(toN);
    };
    requestAnimationFrame(tick);
  }, []);

  const redeemedMessage = useCallback((item) => {
    const cat = (item?.category || "").toLowerCase();
    const name = item?.name || "";
    if (cat.includes("physical") || cat.includes("voucher") || cat.includes("merch")) {
      return __(":name — we've got your request and our team will process it shortly.", {
        name,
      });
    }
    return __(":name is on its way — it'll land in your account shortly.", { name });
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedItem(null);
    setWarning("");
    setRevealPrize(null);
    setRevealPhase("none");
    setRedeeming(false);

    if (activeCardRef.current && typeof activeCardRef.current.focus === "function") {
      activeCardRef.current.focus();
    }
  }, []);

  const openModal = useCallback((item, anchor) => {
    if (item.isLocked) {
      setShakingItemId(item.id);
      window.setTimeout(() => setShakingItemId(""), 420);
    }
    activeCardRef.current = anchor;
    idempotencyKeyRef.current = makeIdempotencyKey();
    setSelectedItem(item);
    setWarning(item.isLocked ? item.lockReason : "");
    setRevealPrize(null);
    setRevealPhase("none");
    setRedeeming(false);
    setModalOpen(true);
  }, []);

  const goEarnMore = useCallback(() => {
    closeModal();
    history.push("/missions");
  }, [closeModal, history]);

  useEffect(() => {
    document.body.classList.toggle("store-modal-open", modalOpen);
    return () => document.body.classList.remove("store-modal-open");
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return undefined;

    const id = window.setTimeout(() => closeBtnRef.current?.focus(), 80);
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
        return;
      }
      if (e.key !== "Tab" || !modalRef.current) return;
      const focusable = modalRef.current.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [modalOpen, closeModal]);

  useEffect(() => {
    if (!selectedItem || revealPhase !== "none") return;
    const cost = Number(selectedItem.cost || 0);
    if (selectedItem.isLocked) {
      setWarning(selectedItem.lockReason || "");
      return;
    }
    if (balanceKnown && cost > balance) {
      setWarning(
        __("You need :count more Clover Coins.", { count: formatCc(cost - balance) })
      );
      return;
    }
    setWarning("");
  }, [selectedItem, balance, balanceKnown, revealPhase]);

  const pickMysteryPrizeMock = useCallback(
    (item) => {
      const pool = STORE_MYSTERY_PRIZES[item.name] || STORE_MYSTERY_PRIZES["Silver Chest"];
      return pool[Math.floor(Math.random() * pool.length)];
    },
    []
  );

  const onConfirm = useCallback(async () => {
    if (!selectedItem) return;
    if (redeeming) return;

    const cost = Number(selectedItem.cost || 0);
    const category = selectedItem.category;

    // Stage 1: initial redeem / open chest
    if (revealPhase === "none") {
      if (selectedItem.isLocked || (balanceKnown && cost > balance)) return;

      setRedeeming(true);
      try {
        if (!preview) {
          const response = await api.post("/store/redeem", {
            item_id: selectedItem.id,
            idempotency_key: idempotencyKeyRef.current,
          });
          const result = response?.data?.data;
          if (response?.status !== 200 || !result) {
            const message = response?.data?.message;
            setWarning(message || __("Redeem failed"));
            if (response?.status === 422 && message) {
              Swal.fire(__("Redeem failed"), message, "error");
            }
            setRedeeming(false);
            return;
          }

          const nextBalance = Number(result.new_balance);
          if (Number.isFinite(nextBalance)) {
            animateBalance(balance, nextBalance);
          }

          const reveal = mapPrizeToReveal(result.prize);
          if (reveal) {
            setRevealPrize(reveal);
            setRevealPhase("pending");
            setWarning("");
            setRedeeming(false);
            return;
          }

          closeModal();
          showHubToast(__("Redeemed"), redeemedMessage(selectedItem));
          setRedeeming(false);
          return;
        }

        // Mock mode: client-side reveal
        const old = balance;
        const next = old - cost;
        animateBalance(old, next);
        setBalance(next);

        if (category === "mystery") {
          const prize = pickMysteryPrizeMock(selectedItem);
          setRevealPrize({
            artId: prize.artId,
            label: prize.label,
            sub: prize.sub,
          });
          setRevealPhase("pending");
          setWarning("");
        } else {
          closeModal();
          showHubToast(__("Redeemed"), redeemedMessage(selectedItem));
        }
      } catch (e) {
        // api.post never rejects; this only catches unexpected local errors.
        setWarning(__("Redeem failed"));
      } finally {
        setRedeeming(false);
      }

      return;
    }

    // Stage 2: turn the chest around
    if (revealPhase === "pending") {
      setRevealPhase("revealed");
      return;
    }

    // Stage 3: finish
    if (revealPhase === "revealed") {
      closeModal();
      if (revealPrize) showHubToast(__("Added"), revealPrize.label);
    }
  }, [
    selectedItem,
    revealPhase,
    redeeming,
    balance,
    balanceKnown,
    preview,
    animateBalance,
    closeModal,
    pickMysteryPrizeMock,
    redeemedMessage,
    revealPrize,
  ]);

  const confirmLabel = useCallback(() => {
    if (!selectedItem) return __("Redeem");
    if (revealPhase === "pending") return __("Reveal");
    if (revealPhase === "revealed") return __("Lucky me!");
    if (selectedItem.isLocked) return __("Locked");
    const cost = Number(selectedItem.cost || 0);
    if (balanceKnown && cost > balance) return __("Earn more coins");
    if (selectedItem.category === "mystery") return __("Open chest");
    return __("Redeem");
  }, [selectedItem, revealPhase, balance, balanceKnown]);

  const headline = useMemo(() => {
    if (!selectedItem) return "";
    if (revealPhase === "pending") return __("OPENING...");
    if (revealPhase === "revealed") return __("YOU WON");
    return selectedItem.headline;
  }, [selectedItem, revealPhase]);

  const modalDesc = useMemo(() => {
    if (!selectedItem) return "";
    if (revealPhase === "pending") {
      return __("Tap the chest to reveal what is inside.");
    }
    if (revealPhase === "revealed" && revealPrize) {
      return __(":label — :sub is on its way to your account.", {
        label: revealPrize.label,
        sub: revealPrize.sub,
      });
    }
    return selectedItem.desc;
  }, [selectedItem, revealPhase, revealPrize]);

  // Ambient effects (rotating FX pool)
  useEffect(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduced) return undefined;

    const motionFx = [
      "fx-card-wiggle",
      "fx-card-glow",
      "fx-card-float",
      "fx-card-tilt",
      "fx-card-bounce",
      "fx-card-rim",
    ];

    // Ambient FX is a merchandising spotlight, not wallpaper: only featured /
    // mystery / ending-soon cards opt in via data-fx-eligible.
    const eligible = () =>
      cardRefs.current
        .filter(Boolean)
        .filter((c) => c.getAttribute("data-fx-eligible") === "true");

    let pool = eligible();
    if (!pool.length) return undefined;

    const shuffle = (arr) => {
      for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
    };

    shuffle(pool);
    let cursor = 0;

    const nextCard = () => {
      if (cursor >= pool.length) {
        pool = eligible();
        shuffle(pool);
        cursor = 0;
      }
      return pool[cursor++];
    };

    const isBusy = (card) => {
      if (!card || card.style.display === "none") return true;
      const image = card.querySelector(".store-card__image");
      if (image && image.classList.contains("fx-on")) return true;
      const classList = [...card.classList];
      return classList.some((c) => c.startsWith("fx-card-"));
    };

    const fireImageFx = (card) => {
      const image = card.querySelector(".store-card__image");
      if (!image) return;
      image.classList.add("fx-on");
      window.setTimeout(() => image.classList.remove("fx-on"), 2300);
    };

    const fireMotionFx = (card) => {
      const fx = motionFx[Math.floor(Math.random() * motionFx.length)];
      card.classList.add(fx);
      window.setTimeout(() => card.classList.remove(fx), 2700);
    };

    const trigger = () => {
      // Whisper, don't shout: skip work while the tab is backgrounded.
      if (typeof document !== "undefined" && document.hidden) return;
      for (let tries = 0; tries < 6; tries += 1) {
        const card = nextCard();
        if (isBusy(card)) continue;
        if (Math.random() < 0.55) fireImageFx(card);
        else fireMotionFx(card);
        return;
      }
    };

    // Initial stagger
    const t1 = window.setTimeout(trigger, 900);

    const intervalId = window.setInterval(trigger, 4500);

    return () => {
      window.clearTimeout(t1);
      window.clearInterval(intervalId);
    };
  }, [filteredItems]);

  return (
    <div className="store-page container-xxl">
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: STORE_SVG_DEFS }}
      />
      <HubToast />

      <div className="store-hero">
        <div className="store-hero-particles" aria-hidden="true" />
        <div className="store-hero-text">
          <h1 className="store-hero-title">{__("STORE")}</h1>
          <span className="store-hero-sub">
            {__("Spend your Clover Coins")} · {items.length} {__("items")}
          </span>
          {data?.rotationLabel ? (
            <span className="store-hero-rotation">
              <svg viewBox="0 0 16 16" aria-hidden="true" width="14" height="14">
                <use href="#clock" />
              </svg>
              {data.rotationLabel}
            </span>
          ) : null}
        </div>
        <svg
          className="store-hero-deco"
          viewBox="0 0 200 80"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="storeHeroLineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(248,216,112,0)" />
              <stop offset="50%" stopColor="rgba(248,216,112,.55)" />
              <stop offset="100%" stopColor="rgba(248,216,112,0)" />
            </linearGradient>
          </defs>
          <path
            d="M0 40 Q 50 20, 100 40 T 200 40"
            stroke="url(#storeHeroLineGrad)"
            strokeWidth="1"
            fill="none"
          />
          <path
            d="M0 50 Q 50 70, 100 50 T 200 50"
            stroke="url(#storeHeroLineGrad)"
            strokeWidth="1"
            fill="none"
            opacity=".5"
          />
        </svg>
      </div>

      {preview ? (
        <p className="store-page__preview-note" role="status">
          <span aria-hidden="true">🍀 </span>
          {__(
            "Sneak peek — this is sample data while we put the finishing touches on your rewards."
          )}
        </p>
      ) : null}

      <div className="store-toolbar">
        <div
          className="store-filter-chips"
          role="tablist"
          aria-label={__("Filter rewards")}
        >
          {STORE_FILTERS.map((option) => {
            const count =
              option.key === "all" ? items.length : filterCounts[option.key] || 0;
            const active = activeFilter === option.key;
            return (
              <button
                key={option.key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`store-filter-chip${active ? " active" : ""}`}
                onClick={() => setActiveFilter(option.key)}
              >
                <span className="chip-label">{__(option.label)}</span>
                <span className="chip-count">{count}</span>
              </button>
            );
          })}
        </div>

        {balanceKnown ? (
          <div className="cc-hud store-inline-hud">
            <div className="cc-hud__icon">
              <CloverIcon size={20} />
            </div>
            <div>
              <div className="num" aria-hidden="true">
                {formatCc(balance)}
              </div>
              <span className="lbl">{__("Clover Coins")}</span>
              <span aria-live="polite" style={SR_ONLY_STYLE}>
                {formatCc(srBalance)} {__("Clover Coins")}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      <div className="store-grid">
        {filteredItems.map((item, index) => {
          const cost = Number(item.cost || 0);
          const soldOut = item.soldOut === true || item.inStock === false;
          const unaffordable =
            balanceKnown && !item.isLocked && !soldOut && cost > balance;
          const lowStock = item.stockPct > 0 && item.stockPct <= 20;
          const fxEligible =
            item.category === "mystery" ||
            item.tagClass === "hot" ||
            item.tagClass === "limited" ||
            Boolean(item.timerText);
          const activate = (e) => {
            if (soldOut) return;
            openModal(item, e.currentTarget);
          };
          return (
            <article
              key={item.id}
              ref={(el) => {
                cardRefs.current[index] = el;
              }}
              className={[
                "store-card",
                `cat-${item.category}`,
                item.isWide ? "wide" : "",
                item.isLocked ? "locked" : "",
                unaffordable ? "unaffordable" : "",
                soldOut ? "sold-out" : "",
                shakingItemId === item.id ? "shake" : "",
                item.displayMode === "illust" && item.tierClass ? item.tierClass : "",
              ]
                .filter(Boolean)
                .join(" ")}
              role="button"
              tabIndex={soldOut ? -1 : 0}
              aria-disabled={soldOut ? true : undefined}
              aria-label={`${item.title || item.name}, ${formatCc(cost)} ${__(
                "Clover Coins"
              )} — ${soldOut ? __("sold out") : __("open details")}`}
              data-fx-eligible={fxEligible ? "true" : "false"}
              onClick={activate}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  activate(e);
                }
              }}
            >
              {soldOut ? (
                <span className="store-card__soldout-ribbon" aria-hidden="true">
                  {__("SOLD OUT")}
                </span>
              ) : null}
              <div className="store-card__content">
                <div className="store-card__headline">{item.headline}</div>
                <div className="store-card__title">{item.title}</div>
                <p className="store-card__desc">{item.desc}</p>
              </div>

              <CardArt item={item} />

              <div className="store-card__bottom">
                <span className="store-card__price">
                  <CloverIcon size={16} />
                  <b>{formatCc(cost)}</b>
                </span>
                {item.timerText ? (
                  <span className="store-card__timer">
                    <svg viewBox="0 0 16 16" aria-hidden="true">
                      <use href="#clock" />
                    </svg>
                    {item.timerText}
                  </span>
                ) : null}
                {lowStock ? (
                  <span className="store-card__stocklbl">{__("Low stock")}</span>
                ) : null}
                <div
                  className={`store-card__progress${lowStock ? " low" : ""}`}
                  role="progressbar"
                  aria-valuenow={item.stockPct || 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={__("Stock remaining")}
                >
                  <b style={{ width: `${item.stockPct || 0}%` }} />
                </div>
                {unaffordable ? (
                  <span className="store-card__need">
                    {__("Need :count more", { count: formatCc(cost - balance) })}
                  </span>
                ) : null}
                <span className="store-card__arrow" aria-hidden="true">
                  <svg viewBox="0 0 16 16" aria-hidden="true">
                    <use href="#arrow" />
                  </svg>
                </span>
              </div>
            </article>
          );
        })}

        {filteredItems.length === 0 ? (
          items.length === 0 ? (
            <div className="store-empty show">
              <div className="store-empty__ico">
                <CloverCrest size={52} />
              </div>
              <div className="store-empty__title">
                {__("No rewards available yet")}
              </div>
              <div className="store-empty__desc">
                {__("New rewards drop every week — check back soon.")}
              </div>
            </div>
          ) : (
            <div className="store-empty show">
              <div className="store-empty__ico">
                <CloverCrest size={52} />
              </div>
              <div className="store-empty__title">
                {__("Nothing in this category yet")}
              </div>
              <div className="store-empty__desc">
                {__("New rewards drop every week — try browsing everything.")}
              </div>
              <button
                type="button"
                className="store-empty__reset"
                onClick={() => setActiveFilter("all")}
              >
                {__("Show all items")}
              </button>
            </div>
          )
        ) : null}
      </div>

      {modalOpen && selectedItem ? (
        <div
          className="store-modal-backdrop open"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
          aria-hidden={!modalOpen}
        >
          <div
            className="store-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="storeModalTitle"
            aria-describedby="storeModalDesc"
            ref={modalRef}
          >
            <button
              className="m-close"
              ref={closeBtnRef}
              type="button"
              onClick={closeModal}
              aria-label={__("Close")}
            >
              ✕
            </button>

            <div className="modal-head">
              <span
                className="m-tag"
                style={{ display: selectedItem.tag ? "inline-block" : "none" }}
              >
                {selectedItem.tag}
              </span>
            </div>

            <div className="m-art">
              {revealPrize ? (
                <div
                  className={`reveal-stage tier-${selectedItem.tier || "bronze"} phase-${revealPhase}`}
                >
                  <div className="reveal-particles" aria-hidden="true" />
                  <div
                    className={`reveal-card ${
                      revealPhase === "revealed" ? "flip" : ""
                    }`}
                    role={revealPhase === "pending" ? "button" : undefined}
                    tabIndex={revealPhase === "pending" ? 0 : undefined}
                    aria-label={
                      revealPhase === "pending" ? __("Tap to open the chest") : undefined
                    }
                    onClick={revealPhase === "pending" ? onConfirm : undefined}
                    onKeyDown={
                      revealPhase === "pending"
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              onConfirm();
                            }
                          }
                        : undefined
                    }
                  >
                    <div className="reveal-face reveal-front">
                      <div className="shimmer" aria-hidden="true" />
                      <ArtSymbol artId={selectedItem.artId} mode="reveal" />
                    </div>
                    <div className="reveal-face reveal-back">
                      <ArtSymbol artId={revealPrize.artId} mode="reveal" />
                      <div className="prize-label">{revealPrize.label}</div>
                      <div className="prize-sub">{revealPrize.sub}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <ArtSymbol artId={selectedItem.artId} mode="main" />
              )}
            </div>

            <div className="m-headline" aria-live="assertive">
              {headline}
            </div>
            <div className="m-title" id="storeModalTitle">
              {selectedItem.name}
            </div>
            <p className="m-desc" id="storeModalDesc">
              {modalDesc}
            </p>

            <div
              className="m-row"
              style={{ display: revealPrize ? "none" : "flex" }}
            >
              <span className="l">{__("Cost")}</span>
              <span className="v">
                <CloverIcon size={18} />
                <span>{formatCc(selectedItem.cost)}</span>
              </span>
            </div>

            <div className="m-actions">
              <button
                className="btn-secondary"
                type="button"
                onClick={closeModal}
              >
                {revealPrize ? __("Close") : __("Cancel")}
              </button>
              <button
                className="btn-primary"
                type="button"
                onClick={
                  revealPhase === "none" &&
                  !selectedItem.isLocked &&
                  balanceKnown &&
                  Number(selectedItem.cost || 0) > balance
                    ? goEarnMore
                    : onConfirm
                }
                disabled={selectedItem.isLocked || redeeming}
              >
                {confirmLabel()}
              </button>
            </div>

            {warning ? (
              <div className="m-warn" role="alert">
                {warning}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

